/*
 *
 *   SugarWOD
 *   algolia.js
 *
 */

const algoliaSearchInsights = require("search-insights");
const instantsearch = require("instantsearch.js").default;
const { connectAutocomplete } = require("instantsearch.js/es/connectors");
const { configure } = require("instantsearch.js/es/widgets"); // https://www.algolia.com/doc/api-reference/widgets/js/
const {
    algoliaAffiliateLocationToString,
    athleteProfileEl,
} = require("@sugarfrontend/modules/ui_utils");
const fetch = require("node-fetch");

const gymIndexName = "Affiliates";
const athleteIndexName = "Athletes";
const workoutsAnalyticsKey = "12059e10635e59f1a8315ab12393bf86";
let algoliaSearchInsightsInitialized = false;
const ORIGIN_URL = window.location.origin;
const CSRF_PROXY_URL = `${ORIGIN_URL}/proxy/csrf-token-proxy`;

const algoliaClientIndexSearch = async (
    indexName = "",
    searchTerms = "",
    params = {}
) => {
    try {
        const csrfResponse = await fetch(CSRF_PROXY_URL);
        const csrfToken = await csrfResponse.headers.get("X-CSRF-TOKEN");
        const ALGOLIA_SEARCH_INDEX_PROXY_URL = `${ORIGIN_URL}/proxy/algolia-search-index-proxy`;

        const response = await fetch(ALGOLIA_SEARCH_INDEX_PROXY_URL, {
            method: "POST",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json; charset=utf-8",
                "X-CSRF-TOKEN": csrfToken,
            },
            body: JSON.stringify({ indexName, searchTerms, params }),
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        return { error };
    }
};

const algoliaClientIndexMultiQuerySearch = async (
    indexName = "",
    queries = ""
) => {
    try {
        const csrfResponse = await fetch(CSRF_PROXY_URL);
        const csrfToken = await csrfResponse.headers.get("X-CSRF-TOKEN");
        const ALGOLIA_MULTI_SEARCH_PROXY_URL = `${ORIGIN_URL}/proxy/algolia-search-multi-query-proxy`;
        const response = await fetch(ALGOLIA_MULTI_SEARCH_PROXY_URL, {
            method: "POST",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json; charset=utf-8",
                "X-CSRF-TOKEN": csrfToken,
            },
            body: JSON.stringify({ indexName, queries }),
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        return { error };
    }
};

const initializeAlgoliaSearchInsights = async (apiKey = "") => {
    try {
        const csrfResponse = await fetch(CSRF_PROXY_URL);
        const csrfToken = await csrfResponse.headers.get("X-CSRF-TOKEN");
        const ALGOLIA_SEARCH_INSIGHTS_PROXY_URL = `${ORIGIN_URL}/proxy/algolia-search-insights-proxy`;
        const response = await fetch(ALGOLIA_SEARCH_INSIGHTS_PROXY_URL, {
            method: "POST",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json; charset=utf-8",
                "X-CSRF-TOKEN": csrfToken,
            },
            // file deepcode ignore HardcodedNonCryptoSecret: false positive
            body: JSON.stringify({ apiKey }),
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        return { error };
    }
};

const sendAlgoliaSearchInsights = async (params = {}) => {
    try {
        const csrfResponse = await fetch(CSRF_PROXY_URL);
        const csrfToken = await csrfResponse.headers.get("X-CSRF-TOKEN");
        const ALGOLIA_SEND_INSIGHTS_PROXY_URL = `${ORIGIN_URL}/proxy/send-algolia-search-insights-proxy`;
        const response = await fetch(ALGOLIA_SEND_INSIGHTS_PROXY_URL, {
            method: "POST",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json; charset=utf-8",
                "X-CSRF-TOKEN": csrfToken,
            },
            body: JSON.stringify({ params }),
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error(error);
        return { error };
    }
};

const getCustomSearch = (algoliaApiKey) => {
    return {
        async search(request) {
            try {
                const csrfResponse = await fetch(CSRF_PROXY_URL);
                const csrfToken = await csrfResponse.headers.get(
                    "X-CSRF-TOKEN"
                );
                const ALGOLIA_SEARCH_PROXY_URL = `${ORIGIN_URL}/proxy/algolia-search-proxy`;
                const response = await fetch(ALGOLIA_SEARCH_PROXY_URL, {
                    method: "POST",
                    headers: {
                        Accept: "application/json, text/plain, */*",
                        "Content-Type": "application/json; charset=utf-8",
                        "X-CSRF-TOKEN": csrfToken,
                    },
                    body: JSON.stringify({
                        request,
                        apiKeyName: algoliaApiKey,
                    }),
                });
                const data = await response.json();
                return data;
            } catch (error) {
                console.error(error);
                return { error };
            }
        },
    };
};

function initializeAutocomplete(
    indexName,
    algoliaApiKey,
    containerId,
    clickFunction,
    { widgetConfig = {}, renderConfig = {} } = {}
) {
    // new instantsearch instance
    const search = instantsearch({
        indexName: indexName,
        searchClient: getCustomSearch(algoliaApiKey),
    });

    // Create the custom widget
    const customAutocomplete = connectAutocomplete(renderAutocomplete);

    // Instantiate the custom widget
    search.addWidgets([
        customAutocomplete({
            container: "#" + containerId,
            clickFunction: clickFunction,
            renderConfig: renderConfig,
        }),
    ]);

    // optional configurations
    if (!_.isEmpty(widgetConfig)) {
        search.addWidgets([configure(widgetConfig)]);
    }

    search.start();
}

// analytics
async function algoliaSearchConvertedAnalytics(params = {}) {
    await sendAlgoliaSearchInsights({ eventType: "conversion", ...params });
}

async function algoliaSearchClickedAnalytics(params = {}) {
    await sendAlgoliaSearchInsights({ eventType: "click", ...params });
}

async function algoliaFiltersClickedAnalytics(params = {}) {
    await sendAlgoliaSearchInsights({ eventType: "click", ...params });
}

function initializeSearchInsightsIfNecessary() {
    if (algoliaSearchInsightsInitialized) return;
    initializeAlgoliaSearchInsights(workoutsAnalyticsKey).then(
        (response) => (algoliaSearchInsightsInitialized = true)
    );
}

// Helper for the render function
const renderIndexListItem = function (results, renderConfig = {}) {
    const { indexId, hits } = results;

    // build out LI elements for each hit
    return _.map(hits, function (hit, idx) {
        // construct markup
        let hitMarkup = "";

        // default strings based on index
        switch (indexId) {
            case gymIndexName:
                hitMarkup = gymResultString(hit);
                break;
            case athleteIndexName:
                hitMarkup = athleteResultString(hit);
                break;
        }

        // custom format for admin page
        if (renderConfig.adminResults) {
            hitMarkup = adminAthleteResultString(hit);
        }

        // include data attributes for use in click function
        const listItem = $("<li/>")
            .html(hitMarkup)
            .attr("data-index-id", indexId)
            .attr("data-hit-index", idx);

        return listItem[0].outerHTML;
    }).join("");
};

// Create the render function
const renderAutocomplete = (renderOptions, isFirstRender) => {
    const { indices, currentRefinement, refine, widgetParams } = renderOptions;

    // container in widgetParams is an element id
    const containerEl = $(widgetParams.container);
    if (!containerEl) return;

    // default variables if they exist
    let input = $("input", containerEl);
    let ul = $("ul", containerEl);

    if (isFirstRender) {
        // create ui elements
        input = $("<input/>");
        ul = $("<ul/>");

        // add to container
        containerEl.append(input).append(ul);

        // hide by default
        ul.addClass("hidden");

        input
            .addClass("form-control")
            .attr("type", "text")
            .on("input", function () {
                // call refinement with input value
                const inputText = $(this).val();
                refine(inputText);

                // show results
                ul.toggleClass("hidden", inputText.length === 0);
            });
    }

    // default placeholder text 'Find Athletes...'
    let placeholderText =
        "Find " + _.map(indices, "indexName").join(" ") + "...";

    // use placeholder from renderConfig
    const { placeholder } = widgetParams.renderConfig;
    if (placeholder) {
        placeholderText = placeholder;
    }

    // default value and placeholder for input
    input.val(currentRefinement).attr("placeholder", placeholderText);

    // build options LI for each result, pass through renderConfig
    const options = _.map(indices, function (results) {
        return renderIndexListItem(results, widgetParams.renderConfig);
    }).join("");

    // replace results on every render
    ul.html(options);

    $("li", ul).click(function () {
        // determine hit from data attributes
        const indexId = $(this).attr("data-index-id");
        const hitIndex = $(this).attr("data-hit-index");
        const index = _.find(indices, { indexId: indexId });
        const hit = index.hits[hitIndex];

        // hide results
        ul.addClass("hidden");

        // update input with chosen name
        let resultName = "";
        switch (indexId) {
            case gymIndexName:
                resultName = hit.name;
                break;
            case athleteIndexName:
                resultName = hit.displayName;
                break;
        }
        input.val(resultName);

        // callback with hit
        widgetParams.clickFunction(hit);
    });
};

// string functions
function gymResultString(hit) {
    let loc = algoliaAffiliateLocationToString(hit);
    return (
        instantsearch.highlight({ attribute: "name", hit }) +
        (loc.length ? " <small>(" + loc + ")</small>" : "")
    );
}

function athleteResultString(hit) {
    let img = athleteProfileEl(hit.displayName, hit.profileImageURL, 24);
    return (
        img[0].outerHTML +
        " " +
        hit.displayName +
        (hit.email ? " <small>(" + hit.email + ")</small>" : "")
    );
}

function adminAthleteResultString(hit) {
    return (
        '<span style="font-family:monospace; ">[' +
        hit.parseId +
        "]</span>" +
        '<span class="m-l-xs color-primary">' +
        hit.displayName +
        "</span>" +
        '<small class="m-l-sm">(' +
        hit.affiliateName +
        ")</small>"
    );
}

module.exports = {
    algoliaClientIndexSearch,
    algoliaClientIndexMultiQuerySearch,
    algoliaSearchInsights,
    algoliaFiltersClickedAnalytics,
    algoliaSearchConvertedAnalytics,
    algoliaSearchClickedAnalytics,
    initializeAutocomplete,
    initializeAlgoliaSearchInsights,
};
